import { useState, useEffect, useMemo } from 'react'
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom'
import {
  LayoutDashboard, Users, ClipboardList, Crown, CalendarDays,
  MessageSquare, BarChart3, Settings, LogOut, Menu, X,
  ChevronDown, MapPin, UserCog, Bell, Tags, FileText,
  TrendingUp, PieChart, Map, CreditCard, Building2, Plug, Download,
  Target, Archive, UserCheck, Contact, Image, Tag, MessageCircle, Smartphone,
} from 'lucide-react'
import clsx from 'clsx'
import { FunnelIcon } from '../../components/Logo'
import { Dropdown, DropdownItem } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import type { MenuItem as ApiMenuItem } from '../../contexts/AuthContext'

const iconMap: Record<string, React.ElementType> = {
  LayoutDashboard, Users, ClipboardList, Crown, CalendarDays,
  MessageSquare, BarChart3, Settings, LogOut, MapPin, UserCog,
  Bell, Tags, FileText, TrendingUp, PieChart, Map, CreditCard,
  Building2, Plug, Target, Archive, UserCheck, Contact, Image,
  Tag, MessageCircle, Smartphone,
}

function getIcon(name: string | null): React.ElementType {
  if (!name) return LayoutDashboard
  return iconMap[name] || LayoutDashboard
}

interface LocalMenuItem {
  label: string
  icon: React.ElementType
  path?: string | null
  children?: { label: string; path: string; icon: React.ElementType }[]
}

function apiMenuToLocal(items: ApiMenuItem[]): LocalMenuItem[] {
  return items.map(item => ({
    label: item.label,
    icon: getIcon(item.icon),
    path: item.path,
    children: item.submenu?.map(sub => ({
      label: sub.label,
      path: sub.path || '',
      icon: getIcon(sub.icon),
    })),
  }))
}

export default function AdminClient() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState<string[]>([])
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null)
  const [showInstallTip, setShowInstallTip] = useState(false)
  const { user, logout, organizations, activeOrganization, switchOrganization, menuItems: dynamicMenuItems } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)

  useEffect(() => {
    const handler = (e: Event) => { e.preventDefault(); setDeferredPrompt(e) }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  function handleInstallClick() {
    if (deferredPrompt) {
      deferredPrompt.prompt()
      deferredPrompt.userChoice.then((result: any) => {
        if (result.outcome === 'accepted') setDeferredPrompt(null)
      })
    } else if (isIOS) {
      setShowInstallTip(true)
    } else {
      setShowInstallTip(true)
    }
  }

  const menuItems = useMemo(() => apiMenuToLocal(dynamicMenuItems), [dynamicMenuItems])

  const toggleMenu = (label: string) => {
    setExpandedMenus(prev =>
      prev.includes(label) ? prev.filter(m => m !== label) : [...prev, label]
    )
  }

  const isActive = (path?: string | null) => {
    if (!path) return false
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  // Auto-expandir menus com filho ativo na primeira renderização
  useEffect(() => {
    const autoExpand = menuItems
      .filter(item => item.children?.some(child => isActive(child.path)))
      .map(item => item.label)
    if (autoExpand.length > 0) {
      setExpandedMenus(prev => [...new Set([...prev, ...autoExpand])])
    }
  }, [location.pathname, menuItems])

  const isMenuExpanded = (item: LocalMenuItem) => {
    return expandedMenus.includes(item.label)
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleNavigation = (path: string) => {
    navigate(path)
    setSidebarOpen(false)
  }

  const handleSwitchOrg = async (orgId: number) => {
    if (orgId === activeOrganization?.id) return
    await switchOrganization(orgId)
    // Redireciona para a primeira rota do novo menu
    const firstPath = dynamicMenuItems[0]?.path || '/dashboard'
    navigate(firstPath)
  }

  const renderMenuItem = (item: LocalMenuItem) => {
    const Icon = item.icon
    const hasChildren = item.children && item.children.length > 0
    const active = item.path ? isActive(item.path) : item.children?.some(c => isActive(c.path))
    const expanded = isMenuExpanded(item)

    return (
      <div key={item.label}>
        <button
          onClick={() => {
            if (hasChildren) {
              toggleMenu(item.label)
            } else if (item.path) {
              handleNavigation(item.path)
            }
          }}
          className={clsx(
            'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
            active
              ? 'bg-white/10 text-white'
              : 'text-white/50 hover:bg-white/5 hover:text-white/80'
          )}
        >
          <Icon className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1 text-left">{item.label}</span>
          {hasChildren && (
            <ChevronDown className={clsx('w-4 h-4 transition-transform duration-200', expanded && 'rotate-180')} />
          )}
        </button>

        {hasChildren && expanded && (
          <div className="ml-4 mt-1 space-y-0.5">
            {item.children!.map(child => (
              <button
                key={child.path}
                onClick={() => handleNavigation(child.path)}
                className={clsx(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                  isActive(child.path)
                    ? 'bg-white/10 text-white font-medium'
                    : 'text-white/40 hover:bg-white/5 hover:text-white/70'
                )}
              >
                <child.icon className="w-4 h-4 flex-shrink-0" />
                <span>{child.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-gray-900/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={clsx(
        'fixed top-0 left-0 z-50 h-full w-[260px] bg-brand-950 flex flex-col transition-transform duration-300 lg:translate-x-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-6">
          <Link to="/dashboard" className="flex items-center gap-1.5">
            <FunnelIcon className="w-10" light />
            <span className="text-[20px] font-bold text-white">S<span className="dotted-i">ı</span>spol<span className="dotted-i">ı</span></span>
          </Link>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-white/40 hover:text-white/70">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Org selector inside sidebar */}
        {organizations.length > 0 && (
          <div className="px-4 mb-2">
            <Dropdown align="left" trigger={
              <button className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                <div className="w-8 h-8 bg-brand-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4 text-brand-300" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-white truncate">{activeOrganization?.name || 'Selecionar'}</p>
                  <p className="text-[11px] text-white/40 truncate">{organizations.find(o => o.organization_id === activeOrganization?.id)?.profile_label || ''}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-white/30 flex-shrink-0" />
              </button>
            }>
              <div className="px-3 py-2 border-b border-gray-100 mb-1">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Trocar organização</p>
              </div>
              {organizations.map(org => (
                <DropdownItem
                  key={org.organization_id}
                  onClick={() => handleSwitchOrg(org.organization_id)}
                  className={clsx(activeOrganization?.id === org.organization_id && 'bg-brand-50 text-brand-600')}
                >
                  <Building2 className="w-4 h-4" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate">{org.organization_name}</p>
                    <p className="text-xs text-gray-400">{org.profile_label}</p>
                  </div>
                </DropdownItem>
              ))}
            </Dropdown>
          </div>
        )}

        {/* Menu */}
        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5">
          {menuItems.length > 0 ? menuItems.map(renderMenuItem) : (
            <p className="text-xs text-white/30 px-3 py-2">Carregando menu...</p>
          )}
        </nav>

        {/* Version + Install */}
        <div className="px-6 py-4 border-t border-white/5">
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-white/20">Sispoli v1.0.0</p>
            {!isStandalone && (
              <button onClick={handleInstallClick} className="flex items-center gap-1.5 text-[11px] text-white/40 hover:text-white/70 transition-colors">
                <Download className="w-3 h-3" /> Instalar app
              </button>
            )}
          </div>
          {showInstallTip && (
            <div className="mt-2 p-2.5 bg-white/10 rounded-lg relative">
              <button onClick={() => setShowInstallTip(false)} className="absolute top-1 right-1.5 text-white/30 hover:text-white/60 text-xs">x</button>
              {isIOS ? (
                <p className="text-[11px] text-white/60 leading-relaxed pr-4">
                  Toque em <span className="text-white/90 font-medium">Compartilhar</span> e depois em <span className="text-white/90 font-medium">Adicionar a Tela de Inicio</span>
                </p>
              ) : (
                <p className="text-[11px] text-white/60 leading-relaxed pr-4">
                  No menu do navegador, clique em <span className="text-white/90 font-medium">Instalar aplicativo</span>
                </p>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="lg:ml-[260px]">
        {/* Navbar */}
        <header className="sticky top-0 z-30 h-16 bg-gray-50 flex items-center justify-between px-4 lg:px-8">
          {/* Left: hamburger (mobile) */}
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
            <Menu className="w-5 h-5" />
          </button>

          {/* Center: logo (mobile only) */}
          <div className="lg:hidden flex items-center gap-1.5">
            <FunnelIcon className="w-8" />
            <span className="text-lg font-bold text-brand-900">S<span className="dotted-i">ı</span>spol<span className="dotted-i">ı</span></span>
          </div>

          {/* Left: page title (desktop) */}
          <div className="hidden xl:block" />

          {/* Right: user */}
          <Dropdown trigger={
            <button className="flex items-center gap-3 py-1.5 px-2 -mr-2 rounded-xl hover:bg-gray-50 transition-colors">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-gray-800 leading-tight">{user?.name || 'Usuário'}</p>
                <p className="text-[11px] text-gray-400 leading-tight">{user?.email || ''}</p>
              </div>
              <div className="w-9 h-9 bg-brand-50 rounded-xl flex items-center justify-center">
                <span className="text-sm font-semibold text-brand-600">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
              </div>
            </button>
          }>
            <div className="px-3 py-3 border-b border-gray-100 mb-1">
              <p className="text-sm font-medium text-gray-800">{user?.name}</p>
              <p className="text-xs text-gray-400 mt-0.5">{user?.email}</p>
            </div>
            <DropdownItem onClick={() => navigate('/settings/general')}>
              <Settings className="w-4 h-4" /> Configurações
            </DropdownItem>
            <DropdownItem onClick={handleLogout} danger>
              <LogOut className="w-4 h-4" /> Sair
            </DropdownItem>
          </Dropdown>
        </header>

        {/* Content */}
        <main className="p-5 xl:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
